// components/ModalOrdenCobro.jsx
import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Table } from "react-bootstrap";
import moment from "moment";
import { postOrdenCobro } from "../helpers/rutaOrdenesCobro";
import { generarPDFOrdenCobro } from "../helpers/pdfOrdenCobro";
import { getComandasPendientesByCliente } from "../helpers/rutaComandas";
import { generarPDFSaldoCliente } from "../helpers/pdfSaldoCliente";
import MediosDePago from "./MediosDePago";

// ---------- helpers de canon para API (BACKEND) ----------
const canonTipoAPI = (t) => {
  const s = String(t || "").toLowerCase();
  if (s === "efectivo") return "efectivo";
  if (s === "transferencia") return "transferencia";
  if (s === "cheque" || s === "cheque_tercero" || s === "cheque propio" || s === "cheque_propio") return "cheque_tercero";
  if (s === "echeq" || s === "icheq") return "echeq";
  return "efectivo";
};
const toISO = (d) => (d ? new Date(d).toISOString() : undefined);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Canon para retenciones → minúsculas (lo que acepta el backend)
const canonRet = (t) => {
  const s = String(t || "").trim().toLowerCase();
  if (s.startsWith("iib")) return "iibb";
  if (s.includes("ganan")) return "ganancias";
  if (s.includes("iva")) return "iva";
  if (s.includes("suss") || s.includes("s.u.s.s")) return "suss";
  return null;
};

export default function ModalOrdenCobro({ show, onHide, cliente, comandasPreseleccionadas = [] }) {
  const [pendientes, setPendientes] = useState([]);
  const [seleccion, setSeleccion] = useState([]); // [{_id, codcom, nroviaje, fecha, saldo, total}]
  const [nroOrdenCliente, setNroOrdenCliente] = useState("");
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("");

  const [medios, setMedios] = useState([]);  // <MediosDePago/>
  const [rets, setRets] = useState([]);

  // validación que viene del hijo
  const [okMedios, setOkMedios] = useState(false);

  // ---- totales
  const totalAplic = useMemo(
    () => seleccion.reduce((a, c) => a + Number((c.saldo ?? c.total) || 0), 0),
    [seleccion]
  );
  const totalMedios = useMemo(() => medios.reduce((a, m) => a + Number(m.importe || 0), 0), [medios]);
  const totalRets   = useMemo(() => rets.reduce((a, r) => a + Number(r.importe || 0), 0), [rets]);
  const diff = useMemo(() => (totalMedios + totalRets - totalAplic).toFixed(2), [totalMedios, totalRets, totalAplic]);

  const okSumas  = Math.abs(Number(diff)) <= 0.01;
  const okCampos = !!nroOrdenCliente && (seleccion?.length || 0) > 0;
  const puedeGuardar = okSumas && okCampos && okMedios;

  // ---- carga de pendientes SIEMPRE, y mergeo de preseleccionadas
  useEffect(() => {
    if (!show || !cliente?._id) return;

    (async () => {
      const list = await getComandasPendientesByCliente(cliente._id); // trae saldo por comanda
      const ordenadas = (list || []).slice().sort((a,b)=> new Date(a.fecha||0) - new Date(b.fecha||0));
      setPendientes(ordenadas);

      const mapSel = new Map();
      const seed = [
        ...comandasPreseleccionadas.map(c => {
          const found = ordenadas.find(x => x._id === c._id);
          return {
            _id: c._id,
            codcom:   found?.codcom   ?? c.codcom,
            nroviaje: found?.nroviaje ?? c.nroviaje,
            fecha:    found?.fecha    ?? c.fecha,
            saldo: Number((found?.saldo ?? c.saldo ?? found?.total ?? c.total) || 0),
            total: Number((found?.total ?? c.total) || 0),
          };
        }),
        ...seleccion,
      ];
      seed.forEach(x => mapSel.set(x._id, x));
      setSeleccion(Array.from(mapSel.values()).filter(x => (x.saldo ?? x.total) > 0.01));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cliente?._id]);

  const toggleSeleccion = (c) => {
    setSeleccion(prev => {
      const exists = prev.find(x => x._id === c._id);
      if (exists) return prev.filter(x => x._id !== c._id);
      return [...prev, { ...c, saldo: Number(c.saldo ?? (c.total ?? 0)) }];
    });
  };

  const seleccionarTodas = () => {
    const habilitadas = pendientes
      .filter(p => Number(p.saldo || 0) > 0.01)
      .map(p => ({ ...p, saldo: Number((p.saldo ?? p.total) || 0) }));
    setSeleccion(habilitadas);
  };
  const deseleccionarTodas = () => setSeleccion([]);

  const addRet   = (r) => setRets(prev => [...prev, r]);
  const removeRet = (i) => setRets(prev => prev.filter((_, idx) => idx !== i));

  /** Normaliza un medio individual para el backend — NUNCA envía 'tipo' */
  const serializeMedio = (m) => {
    const metodoPagoId =
      (m?.metodoPago && typeof m.metodoPago === "string" && m.metodoPago) ||
      (m?.metodoPago && typeof m.metodoPago === "object" && m.metodoPago._id) ||
      null;

    // Solo se usa para decidir qué campos opcionales incluir
    const tipoLower = canonTipoAPI(m.tipo);

    // clave: agrego fechaValor y seteo "numero" coherente con el medio
    const base = {
      importe: round2(m.importe),
      fecha: toISO(m.fecha || fecha),
      fechaValor: toISO(m.fechaValor),
      referencia: m.referencia || undefined,
      numero:
        (tipoLower === "cheque_tercero" && (m.nroCheque || m.numero)) ||
        (tipoLower === "echeq" && (m.idICheq || m.numero)) ||
        m.numero || undefined,
      refTransferencia: m.refTransferencia || m.referencia || m.numero || undefined,
    };

    const segunMedio = {
      ...(tipoLower === "efectivo" ? { caja: m.caja || undefined } : {}),
      ...(tipoLower === "transferencia" ? { cuentaBancaria: m.cuentaBancaria || undefined } : {}),
      ...(tipoLower === "cheque_tercero"
        ? {
            nroCheque: m.nroCheque || undefined,
            bancoEmisor: m.bancoEmisor || undefined,
            librador: m.librador || undefined,
            cuitLibrador: m.cuitLibrador || undefined,
            fechaPagoDiferido: toISO(m.fechaPagoDiferido),
          }
        : {}),
      ...(tipoLower === "echeq"
        ? {
            idICheq: m.idICheq || undefined,
            emisor: m.emisor || undefined,
            fechaLiquidacion: toISO(m.fechaLiquidacion),
          }
        : {}),
    };

    // IMPORTANTE: NO enviar 'tipo'
    return {
      ...base,
      ...segunMedio,
      ...(metodoPagoId ? { metodoPago: metodoPagoId } : {}),
    };
  };

  const guardar = async () => {
    if (!nroOrdenCliente) return alert("Ingresá el número de orden del cliente.");
    if (!seleccion.length) return alert("Seleccioná al menos una comanda.");
    if (!okMedios) return alert("Revisá los medios de pago (importe > 0 y campos obligatorios).");
    if (!okSumas) {
      return alert("Los totales no cierran (ΣMedios + ΣRetenciones = ΣAplicaciones).");
    }

    // --- Normalizo medios para el backend (SIN 'tipo') ---
    const mediosCanon = (medios || []).map(serializeMedio);

    // --- Normalizo retenciones ---
    const retencionesCanon = (rets || [])
      .filter(r => (Number(r.importe) || 0) > 0) // no mandes filas vacías
      .map((r) => ({
        tipo: canonRet(r.tipo || r.nombre || r.label), // iibb | ganancias | iva | suss
        numero: r.numero || "",
        fecha: toISO(r.fecha || fecha),
        importe: round2(r.importe),
      }));

    // Validación rápida de retenciones
    if (retencionesCanon.some(r => !r.tipo)) {
      alert("Hay retenciones sin tipo válido. Usá IIBB / GANANCIAS / IVA / SUSS o borrá la fila.");
      return;
    }

    const payload = {
      cliente: cliente._id,
      nroOrdenCliente,
      fecha,
      observaciones,
      aplicaciones: seleccion.map((c) => ({
        comanda: c._id,
        importe: Number(c.saldo ?? c.total),
      })),
      medios: mediosCanon,
      retenciones: retencionesCanon,
      totalAplicaciones: Number(totalAplic.toFixed(2)),
      totalMedios: Number(totalMedios.toFixed(2)),
      totalRetenciones: Number(totalRets.toFixed(2)),
    };

    console.log("OC payload >>>", JSON.stringify(payload, null, 2));

    const res = await postOrdenCobro(payload);

    if (res?.ok) {
      alert("Orden de cobro guardada correctamente.");
      try {
        generarPDFOrdenCobro(res.ordenCobro);
      } catch (e) {
        console.error("Error generando PDF:", e);
      }
      onHide(true);
    } else {
      console.error("OC error:", res);
      alert(res?.msg || res?.error || "Error al guardar la orden.");
    }
  };

  // ---- Exportar SOLO las seleccionadas (las tildadas) ----
  const exportarSeleccionadasPDF = () => {
    if (!(seleccion?.length)) {
      alert("No hay comandas seleccionadas para exportar.");
      return;
    }
    const toNum  = v => Number(v || 0);
    const toDate = v => (v ? new Date(v) : null);
    const toStr  = v => (v == null ? "" : String(v));
    const pick = (obj, ...keys) => keys.find(k => obj?.[k] != null);

    const items = seleccion.map(c => {
      const numero   = toStr(c[pick(c,"codcom","numero","nrodecomanda","num")] || "");
      const nroviaje = toStr(c[pick(c,"nroviaje","nroViaje","nro_viaje","nro","nrovia","nro_via")] || "");
      const fecha    = toDate(c[pick(c,"fecha","fec","fech")] || null);
      const total    = toNum(c[pick(c,"total","Total")] || 0);
      const saldo    = toNum(c[pick(c,"saldo","Saldo")] || 0);
      const aplicado = Math.max(total - saldo, 0);
      return { numero, nroviaje, fecha, total, aplicado, saldo };
    });

    const totalSaldo = items.reduce((s,i) => s + i.saldo, 0);
    const clienteNombre = (cliente?.razonsocial) || (cliente?.nombre) || (cliente?.nombreFantasia) || "";
    const fechaCorte = toDate(fecha || new Date());

    generarPDFSaldoCliente({
      clienteNombre,
      fechaCorte,
      data: { totalSaldoComandas: totalSaldo, comandasConSaldo: items, ordenesCobroAbiertas: [] },
      titulo: "INFORME DE SALDOS PENDIENTES",
    });
  };

  // ---------- Exportar TODO lo que ves en la grilla ----------
  const exportarTodoPDF = () => {
    if (!(pendientes?.length)) {
      alert("No hay comandas pendientes para exportar.");
      return;
    }
    const pick = (obj, ...keys) => keys.find(k => obj?.[k] != null);
    const toNum  = v => Number(v || 0);
    const toDate = v => (v ? new Date(v) : null);
    const toStr  = v => (v == null ? "" : String(v));

    const items = pendientes.map(c => {
      const numero   = toStr(c[pick(c,"codcom","numero","nrodecomanda","num")] || "");
      const nroviaje = toStr(c[pick(c,"nroviaje","nroViaje","nro_viaje","nro","nrovia","nro_via")] || "");
      const fecha    = toDate(c[pick(c,"fecha","fec","fech")] || null);
      const total    = toNum(c[pick(c,"total","Total")] || 0);
      const saldo    = toNum(c[pick(c,"saldo","Saldo")] || 0);
      const aplicado = Math.max(total - saldo, 0);
      return { numero, nroviaje, fecha, total, aplicado, saldo };
    });

    const totalSaldo = items.reduce((s,i) => s + i.saldo, 0);
    const clienteNombre = (cliente?.razonsocial) || (cliente?.nombre) || (cliente?.nombreFantasia) || "";
    const fechaCorte = toDate(fecha || new Date());

    generarPDFSaldoCliente({
      clienteNombre,
      fechaCorte,
      data: { totalSaldoComandas: totalSaldo, comandasConSaldo: items, ordenesCobroAbiertas: [] },
      titulo: "INFORME DE SALDOS PENDIENTES",
    });
  };

  const $ = (n) => Number(n||0).toLocaleString("es-AR",{minimumFractionDigits:2, maximumFractionDigits:2});

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Nueva Orden de Cobro</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-2">
          <Col md={4}>
            <Form.Label>Cliente</Form.Label>
            <Form.Control value={cliente?.razonsocial || ""} readOnly />
          </Col>
          <Col md={4}>
            <Form.Label>N° Orden del Cliente</Form.Label>
            <Form.Control value={nroOrdenCliente} onChange={e => setNroOrdenCliente(e.target.value)} />
          </Col>
          <Col md={4}>
            <Form.Label>Fecha</Form.Label>
            <Form.Control type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
          </Col>
        </Row>

        {/* === Toolbar de la grilla === */}
        <Row className="align-items-center mb-2">
          <Col className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <b className="me-3">Comandas pendientes</b>
            <div className="d-inline-flex flex-wrap gap-2">
              <Button size="sm" variant="outline-secondary" onClick={seleccionarTodas}>
                Tildar todas
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={deseleccionarTodas}>
                Destildar todas
              </Button>
              <Button size="sm" variant="success" onClick={exportarTodoPDF} style={{ minWidth: 150 }}>
                Exportar TODO PDF
              </Button>
              <Button
                size="sm"
                variant="outline-success"
                onClick={exportarSeleccionadasPDF}
                disabled={!seleccion.length}
                style={{ minWidth: 190 }}
              >
                Exportar SELECCIONADAS PDF
              </Button>
              {Boolean(seleccion.length) && (
                <span className="ms-2">Seleccionadas: <b>{seleccion.length}</b></span>
              )}
            </div>
          </Col>
        </Row>

        {/* === Grilla de pendientes === */}
        <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e0e0e0", position: "relative" }}>
          <Table size="sm" bordered hover className="m-0">
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 2 }}>
                <th style={{ width: 40, textAlign: "center" }}></th>
                <th>Comanda</th>
                <th>Viaje</th>
                <th>Fecha</th>
                <th>Factura</th>
                <th style={{ textAlign: "right" }}>Saldo</th>
                <th style={{ textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((c) => {
                const checked = !!seleccion.find((x) => x._id === c._id);
                const disabled = Number(c.saldo || 0) <= 0.01;
                const inputId = `sel-${c._id}`;
                return (
                  <tr key={c._id} style={disabled ? { opacity: 0.5 } : undefined}>
                    <td style={{ width: 40, textAlign: "center" }}>
                      <input
                        id={inputId}
                        type="checkbox"
                        className="form-check-input m-0 p-0"
                        style={{ marginLeft: 0, position: "static" }}
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggleSeleccion(c)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>

                    <td>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        #{String(c.codcom ?? "").padStart(6, "0")}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        {c.nroviaje}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        {c.fecha ? moment(c.fecha).format("DD/MM/YYYY") : ""}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        {c.factura || ""}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        ${$(c.saldo ?? 0)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={inputId} className="w-100 m-0" style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                        ${$(c.total ?? 0)}
                      </label>
                    </td>
                  </tr>
                );
              })}
              {!pendientes.length && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    Sin pendientes para este cliente
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* === Medios de pago === */}
        <Row className="mt-3">
          <Col md={12}>
            <h6>Medios de pago</h6>
            <MediosDePago
              medios={medios}
              setMedios={setMedios}
              onTotalsChange={() => {}}
              onValidityChange={({ valid }) => setOkMedios(valid)}
            />
            {!okMedios && (
              <div className="mt-2 text-danger">
                Revisá los medios: importe &gt; 0 y, si es EFECTIVO, seleccioná una caja; si es TRANSFERENCIA, una cuenta.
              </div>
            )}
          </Col>
        </Row>

        {/* === Retenciones === */}
        <Row className="mt-3">
          <Col md={12}>
            <h6>Retenciones</h6>
            <Button size="sm" onClick={() => addRet({ tipo: "iibb", importe: 0, fecha })}>+ IIBB</Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "ganancias", importe: 0, fecha })}>+ Ganancias</Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "iva", importe: 0, fecha })}>+ IVA</Button>{" "}
            <Button size="sm" onClick={() => addRet({ tipo: "suss", importe: 0, fecha })}>+ SUSS</Button>
            <Table size="sm" className="mt-2">
              <thead><tr><th>Tipo</th><th>N°</th><th>Fecha</th><th>Importe</th><th></th></tr></thead>
              <tbody>
                {rets.map((r, i) => (
                  <tr key={i}>
                    <td>{r.tipo}</td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={r.numero || ""}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, numero: e.target.value } : x))}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="date"
                        value={r.fecha || fecha}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, fecha: e.target.value } : x))}
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.importe}
                        onChange={(e) => setRets(prev => prev.map((x, idx) => idx === i ? { ...x, importe: Number(e.target.value) } : x))}
                      />
                    </td>
                    <td><Button variant="outline-danger" size="sm" onClick={() => removeRet(i)}>x</Button></td>
                  </tr>
                ))}
                {!rets.length && <tr><td colSpan={5} className="text-muted">Agregá retenciones si corresponde</td></tr>}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control as="textarea" rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={4}><b>Total aplicaciones:</b> ${$(totalAplic)}</Col>
          <Col md={4}><b>Σ Medios:</b> ${$(totalMedios)}</Col>
          <Col md={4}><b>Σ Retenciones:</b> ${$(totalRets)}</Col>
        </Row>
        <Row>
          <Col md={12}>
            <b>Diferencia:</b>{" "}
            <span style={{color: Math.abs(Number(diff))<0.01 ? "green" : "red"}}>
              {diff}
            </span>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>Cancelar</Button>
        <Button
          variant="primary"
          onClick={guardar}
          disabled={!puedeGuardar}
        >
          Guardar y generar PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
